import React from 'react';
import { Link } from '@mui/material';
import type { LinkProps } from '@mui/material';

export interface LinkComponentProps {
  children: React.ReactNode;
  href: string;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  variant?: 'inherit' | 'body1' | 'body2' | 'subtitle1' | 'subtitle2' | 'caption' | 'button' | 'overline' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  color?: 'inherit' | 'primary' | 'secondary' | 'textPrimary' | 'textSecondary' | 'error';
  underline?: 'none' | 'hover' | 'always';
  target?: '_blank' | '_self' | '_parent' | '_top';
  rel?: string;
  disabled?: boolean;
  sx?: LinkProps['sx'];
}

/**
 * LinkComponent - Reusable Link Component
 * 
 * A standardized link component that provides:
 * - Consistent styling and behavior across the application
 * - Support for various link variants and colors
 * - Flexible underline behavior
 * - External link support with proper security attributes
 * - Disabled state support
 * - Custom styling options
 */
export const LinkComponent: React.FC<LinkComponentProps> = ({
  children,
  href,
  onClick,
  variant = 'body2',
  color = 'primary',
  underline = 'hover',
  target,
  rel,
  disabled = false,
  sx,
}) => {
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (disabled) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };

  // Auto-add security attributes for external links
  const isExternalLink = target === '_blank' || href.startsWith('http');
  const linkRel = isExternalLink ? 'noopener noreferrer' : rel;

  return (
    <Link
      href={disabled ? undefined : href}
      onClick={handleClick}
      variant={variant}
      color={disabled ? 'textSecondary' : color}
      underline={disabled ? 'none' : underline}
      target={target}
      rel={linkRel}
      sx={{
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        textDecoration: underline === 'none' ? 'none' : undefined,
        '&:hover': {
          textDecoration: disabled 
            ? 'none' 
            : underline === 'hover' || underline === 'always' 
              ? 'underline' 
              : 'none',
          opacity: disabled ? 0.6 : 0.8,
        },
        transition: 'all 0.2s ease-in-out',
        ...sx,
      }}
    >
      {children}
    </Link>
  );
};

export default LinkComponent;